import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star, Camera, Quote, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import GoogleReviews from "@/components/GoogleReviews";

export const photoSrc = (url) => (url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

export function Stars({ n, size = 13, testId }) {
  return (
    <span className="flex gap-0.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} className={i <= n ? "fill-gold text-gold" : "text-ink/20"} />)}
    </span>
  );
}

function ReviewDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", location: "", trip: "", rating: 5, text: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const pick = (f) => {
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) return toast.error("Photo must be under 6 MB");
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return toast.error("Add your name and a few words");
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (photo) fd.append("photo", photo);
    try {
      await api.post("/reviews", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thank you! Your review will appear once our team approves it.");
      setForm({ name: "", location: "", trip: "", rating: 5, text: "" });
      setPhoto(null); setPreview("");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-white border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-lg bg-sand border-ink/10 max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-ink">Share your Puri moment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={inputCls} data-testid="review-name-input" />
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City (e.g. Kolkata)" className={inputCls} data-testid="review-location-input" />
          </div>
          <input value={form.trip} onChange={(e) => setForm({ ...form, trip: e.target.value })} placeholder="What did you do? (e.g. Konark ride on the Meteor 350)" className={inputCls} data-testid="review-trip-input" />
          <div className="flex items-center gap-3">
            <p className="text-[11px] font-mono uppercase tracking-widest text-ink/60">Rating</p>
            <div className="flex gap-1" data-testid="review-rating-picker">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" onClick={() => setForm({ ...form, rating: i })} data-testid={`review-rating-${i}`}>
                  <Star size={22} className={i <= form.rating ? "fill-gold text-gold" : "text-ink/20"} />
                </button>
              ))}
            </div>
          </div>
          <textarea rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Tell future travellers about your trip..." className={inputCls} data-testid="review-text-input" />
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="review-photo-input" />
          {preview ? (
            <div className="relative rounded-xl overflow-hidden h-44" data-testid="review-photo-preview">
              <img src={preview} alt="preview" className="w-full h-full object-cover" />
              <button type="button" onClick={() => { setPhoto(null); setPreview(""); }} className="absolute top-2 right-2 bg-ink/80 text-sand rounded-full p-1.5" data-testid="review-photo-remove"><X size={14} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="review-photo-button"
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-ink/20 rounded-xl py-5 text-sm text-ink/60 hover:border-terra hover:text-terra transition-colors">
              <ImagePlus size={17} /> Add a photo from your trip (optional)
            </button>
          )}
          <button type="submit" disabled={busy} data-testid="review-submit-button"
            className="w-full bg-terra text-sand font-semibold py-3 rounded-full hover:bg-ink transition-colors disabled:opacity-60">
            {busy ? "Uploading..." : "Submit review"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  return (
    <section id="reviews" className="py-20 lg:py-28 bg-surface" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">08 · Traveller Moments</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink">
              Real rides. <span className="italic text-terra">Real smiles.</span>
            </h2>
            {avg && (
              <p className="mt-4 flex items-center gap-2 text-ink/70" data-testid="reviews-average">
                <Stars n={Math.round(avg)} size={16} /> <span className="font-semibold text-ink">{avg}</span> from {reviews.length} traveller{reviews.length > 1 ? "s" : ""}
              </p>
            )}
          </motion.div>
          <button onClick={() => setOpen(true)} data-testid="review-open-button"
            className="flex items-center gap-2 bg-ink text-sand font-semibold px-6 py-3 rounded-full hover:bg-terra transition-colors self-start">
            <Camera size={16} /> Share your Puri moment
          </button>
        </div>

        <GoogleReviews />

        {reviews.length === 0 ? (
          <div className="bg-white border border-dashed border-ink/20 rounded-2xl p-10 text-center text-ink/50 text-sm" data-testid="reviews-empty">
            Be the first to share a photo from your Random Rider trip.
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5" data-testid="reviews-grid">
            {reviews.map((r, i) => (
              <motion.article key={r.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="break-inside-avoid bg-white rounded-2xl overflow-hidden border border-ink/10 hover:shadow-xl hover:shadow-ink/10 transition-shadow duration-500"
                data-testid={`review-card-${r.id.slice(0, 8)}`}>
                {r.photo_url && <img src={photoSrc(r.photo_url)} alt={`${r.name}'s trip`} loading="lazy" className="w-full object-cover max-h-72" />}
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <Stars n={r.rating} />
                    <Quote size={16} className="text-terra/40" />
                  </div>
                  <p className="mt-3 text-sm text-ink/80 leading-relaxed">{r.text}</p>
                  <div className="mt-4 pt-3 border-t border-ink/10">
                    <p className="font-semibold text-ink text-sm">{r.name}{r.location && <span className="text-ink/40 font-normal"> · {r.location}</span>}</p>
                    {r.trip && <p className="text-[11px] text-terra mt-0.5">{r.trip}</p>}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
      <ReviewDialog open={open} onClose={() => setOpen(false)} onSaved={load} />
    </section>
  );
}
